import React, { useState, useEffect } from 'react';
import { memoryUtils } from '../../lib/utils';

interface MemoryInfo {
  used: number;
  total: number;
  limit: number;
}

interface NetworkInfo {
  totalRequests: number;
  activeRequests: number;
  cacheHits: number;
  cacheMisses: number;
}

interface CacheInfo {
  key: string;
  size: number;
  hasData: boolean;
  extra?: string;
}

interface ComprehensiveMemoryInfo {
  jsMemory: MemoryInfo | null;
  domNodeCount: number;
  eventListenerCount: number;
  imageMemory: {
    totalSize: number;
    imageCount: number;
    totalImages: number;
  };
  storageUsage: {
    localStorage: number;
    sessionStorage: number;
    localStorageKeys: number;
    sessionStorageKeys: number;
  };
  webglMemory: any;
  mediaMemory: {
    audioCount: number;
    videoCount: number;
    playingAudio: number;
    playingVideo: number;
  };
  timestamp: number;
}

interface MemoryMonitorProps {
  isStandalone?: boolean;
}

export const MemoryMonitor: React.FC<MemoryMonitorProps> = ({ isStandalone = false }) => {
  const [memoryInfo, setMemoryInfo] = useState<MemoryInfo | null>(null);
  const [comprehensiveInfo, setComprehensiveInfo] = useState<ComprehensiveMemoryInfo | null>(null);
  const [networkInfo, setNetworkInfo] = useState<NetworkInfo>({
    totalRequests: 0,
    activeRequests: 0,
    cacheHits: 0,
    cacheMisses: 0
  });
  const [memoryLeaks, setMemoryLeaks] = useState<string[]>([]);
  const [isVisible, setIsVisible] = useState(false);
  const [activeTab, setActiveTab] = useState<'memory' | 'network' | 'cache' | 'leaks'>('memory');

  useEffect(() => {
    const updateMemoryInfo = () => {
      const info = memoryUtils.getMemoryInfo();
      const comprehensive = memoryUtils.getComprehensiveMemoryInfo();
      const leaks = memoryUtils.checkForMemoryLeaks();
      
      setMemoryInfo(info);
      setComprehensiveInfo(comprehensive);
      setMemoryLeaks(leaks);
    };

    updateMemoryInfo();
    const interval = setInterval(updateMemoryInfo, 5000); // Update every 5 seconds

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    // Monitor network requests
    const originalFetch = window.fetch;
    let totalRequests = 0;
    let activeRequests = 0;

    window.fetch = async (...args) => {
      totalRequests++;
      activeRequests++;
      
      try {
        const result = await originalFetch(...args);
        activeRequests--;
        return result;
      } catch (error) {
        activeRequests--;
        throw error;
      }
    };

    const updateNetworkInfo = () => {
      setNetworkInfo(prev => ({
        ...prev,
        totalRequests,
        activeRequests
      }));
    };

    const interval = setInterval(updateNetworkInfo, 2000);
    return () => {
      clearInterval(interval);
      window.fetch = originalFetch;
    };
  }, []);

  const handleClearCaches = () => {
    memoryUtils.clearAllCaches();
    memoryUtils.forceGC();
    // Update memory info after clearing
    setTimeout(() => {
      const info = memoryUtils.getMemoryInfo();
      const comprehensive = memoryUtils.getComprehensiveMemoryInfo();
      const leaks = memoryUtils.checkForMemoryLeaks();
      
      setMemoryInfo(info);
      setComprehensiveInfo(comprehensive);
      setMemoryLeaks(leaks);
    }, 100);
  };

  const getCacheInfo = (): CacheInfo[] => {
    const cacheKeys = [
      'wouldYouRather_usedQuestions',
      'wouldYouRatherV1_usedQuestions_en',
      'wouldYouRatherV1_usedQuestions_es', 
      'wouldYouRatherV1_usedQuestions_fr',
      'wouldYouRatherV1_usedQuestions_hi'
    ];
    
    const cacheInfo: CacheInfo[] = cacheKeys.map(key => {
      const value = sessionStorage.getItem(key);
      return {
        key,
        size: value ? JSON.stringify(value).length : 0,
        hasData: !!value
      };
    });

    // Add service cache info
    try {
          // CDN-based loading doesn't use enhancedGameDataService anymore
    const serviceCache = { message: 'CDN-based loading active' };
      cacheInfo.push({
        key: 'Enhanced Game Service Cache',
        size: serviceCache.totalMemoryUsage || 0,
        hasData: serviceCache.totalQuestions > 0,
        extra: `${serviceCache.languages} langs, ${serviceCache.totalQuestions} questions`
      });
    } catch (error) {
      // Service not loaded yet
    }

    return cacheInfo;
  };

  // For standalone mode, always show the monitor
  if (isStandalone) {
    if (!memoryInfo) {
      return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
            <p className="text-gray-600 dark:text-gray-400">Loading memory monitor...</p>
          </div>
        </div>
      );
    }

    const usagePercentage = (memoryInfo.used / memoryInfo.limit) * 100;
    const isHighUsage = usagePercentage > 80;

    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-4">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 mb-6">
            <div className="flex justify-between items-center mb-4">
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Performance Monitor</h1>
              <a 
                href="/" 
                className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium"
              >
                ← Back to App
              </a>
            </div>
            
            {/* Memory Overview */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                <div className="text-sm text-blue-600 dark:text-blue-400">JS Memory Used</div>
                <div className="text-2xl font-bold text-blue-900 dark:text-blue-100">
                  {memoryInfo.used}MB
                </div>
              </div>
              <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg">
                <div className="text-sm text-green-600 dark:text-green-400">JS Memory Total</div>
                <div className="text-2xl font-bold text-green-900 dark:text-green-100">
                  {memoryInfo.total}MB
                </div>
              </div>
              <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg">
                <div className="text-sm text-purple-600 dark:text-purple-400">JS Memory Limit</div>
                <div className="text-2xl font-bold text-purple-900 dark:text-purple-100">
                  {memoryInfo.limit}MB
                </div>
              </div>
              <div className={`p-4 rounded-lg ${isHighUsage ? 'bg-red-50 dark:bg-red-900/20' : 'bg-yellow-50 dark:bg-yellow-900/20'}`}>
                <div className={`text-sm ${isHighUsage ? 'text-red-600 dark:text-red-400' : 'text-yellow-600 dark:text-yellow-400'}`}>
                  Usage
                </div>
                <div className={`text-2xl font-bold ${isHighUsage ? 'text-red-900 dark:text-red-100' : 'text-yellow-900 dark:text-yellow-100'}`}>
                  {usagePercentage.toFixed(1)}%
                </div>
              </div>
            </div>

            {/* Memory Bar */}
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-4 mb-4">
              <div 
                className={`h-4 rounded-full ${
                  isHighUsage ? 'bg-red-500' : 'bg-green-500'
                }`}
                style={{ width: `${Math.min(usagePercentage, 100)}%` }}
              ></div>
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
            <div className="flex space-x-2 mb-6">
              <button
                onClick={() => setActiveTab('memory')}
                className={`px-4 py-2 text-sm font-medium rounded-lg ${
                  activeTab === 'memory' 
                    ? 'bg-blue-500 text-white' 
                    : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                }`}
              >
                Memory Details
              </button>
              <button
                onClick={() => setActiveTab('network')}
                className={`px-4 py-2 text-sm font-medium rounded-lg ${
                  activeTab === 'network' 
                    ? 'bg-blue-500 text-white' 
                    : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                }`}
              >
                Network
              </button>
              <button
                onClick={() => setActiveTab('cache')}
                className={`px-4 py-2 text-sm font-medium rounded-lg ${
                  activeTab === 'cache' 
                    ? 'bg-blue-500 text-white' 
                    : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                }`}
              >
                Cache
              </button>
              <button
                onClick={() => setActiveTab('leaks')}
                className={`px-4 py-2 text-sm font-medium rounded-lg ${
                  activeTab === 'leaks' 
                    ? 'bg-blue-500 text-white' 
                    : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                }`}
              >
                Memory Leaks
              </button>
            </div>

            {/* Memory Tab */}
            {activeTab === 'memory' && (
              <div className="space-y-4">
                {comprehensiveInfo && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                      <h3 className="font-semibold mb-2">DOM & Events</h3>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span>DOM Nodes:</span>
                          <span className="font-mono">{comprehensiveInfo.domNodeCount}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Event Listeners:</span>
                          <span className="font-mono">{comprehensiveInfo.eventListenerCount}</span>
                        </div>
                      </div>
                    </div>
                    <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                      <h3 className="font-semibold mb-2">Media & Storage</h3>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span>Images:</span>
                          <span className="font-mono">{comprehensiveInfo.imageMemory.imageCount}/{comprehensiveInfo.imageMemory.totalImages}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Image Memory:</span>
                          <span className="font-mono">{comprehensiveInfo.imageMemory.totalSize}MB</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Storage:</span>
                          <span className="font-mono">{comprehensiveInfo.storageUsage.localStorage + comprehensiveInfo.storageUsage.sessionStorage}KB</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Network Tab */}
            {activeTab === 'network' && (
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                    <h3 className="font-semibold mb-2">Network Activity</h3>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span>Total Requests:</span>
                        <span className="font-mono">{networkInfo.totalRequests}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Active Requests:</span>
                        <span className={`font-mono ${networkInfo.activeRequests > 5 ? 'text-yellow-500' : ''}`}>
                          {networkInfo.activeRequests}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                    <h3 className="font-semibold mb-2">Cache Performance</h3>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span>Cache Hits:</span>
                        <span className="font-mono text-green-500">{networkInfo.cacheHits}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Cache Misses:</span>
                        <span className="font-mono text-red-500">{networkInfo.cacheMisses}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Cache Tab */}
            {activeTab === 'cache' && (
              <div className="space-y-4">
                <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                  <h3 className="font-semibold mb-4">Cache Status</h3>
                  <div className="space-y-2">
                    {getCacheInfo().map((cache, index) => (
                      <div key={index} className="flex justify-between items-center p-2 bg-white dark:bg-gray-600 rounded">
                        <span className="truncate max-w-[200px]" title={cache.key}>
                          {cache.key.split('_').pop() || cache.key}
                        </span>
                        <div className="flex items-center space-x-4">
                          <span className={cache.hasData ? 'text-green-500' : 'text-gray-400'}>
                            {cache.hasData ? '●' : '○'}
                          </span>
                          <span className="text-sm text-gray-500 font-mono">
                            {cache.size}B
                          </span>
                          {cache.extra && (
                            <span className="text-xs text-blue-500" title={cache.extra}>
                              ({cache.extra})
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Leaks Tab */}
            {activeTab === 'leaks' && (
              <div className="space-y-4">
                {memoryLeaks.length === 0 ? (
                  <div className="text-center py-8">
                    <div className="text-green-500 text-4xl mb-2">✅</div>
                    <p className="text-green-600 dark:text-green-400 font-medium">No memory leaks detected</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {memoryLeaks.map((leak, index) => (
                      <div key={index} className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-4 rounded-lg">
                        <div className="flex items-start">
                          <span className="text-red-500 mr-2">⚠️</span>
                          <span className="text-red-700 dark:text-red-300">{leak}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                
                {comprehensiveInfo && (
                  <div className="mt-6 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                    <h3 className="font-semibold mb-2">Memory Breakdown</h3>
                    <div className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
                      <div>• JavaScript heap (shown above)</div>
                      <div>• DOM nodes & layout</div>
                      <div>• Images, fonts, resources</div>
                      <div>• Network cache</div>
                      <div>• Extensions & browser overhead</div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Action Buttons */}
            <div className="mt-6 grid grid-cols-1 md:grid-cols-4 gap-2">
              <button
                onClick={handleClearCaches}
                className="bg-red-500 hover:bg-red-600 text-white py-2 px-4 rounded-lg text-sm font-medium"
              >
                Clear All Caches
              </button>
              <button
                onClick={() => {
                  memoryUtils.forceGC();
                  setTimeout(() => {
                    const info = memoryUtils.getMemoryInfo();
                    const comprehensive = memoryUtils.getComprehensiveMemoryInfo();
                    const leaks = memoryUtils.checkForMemoryLeaks();
                    
                    setMemoryInfo(info);
                    setComprehensiveInfo(comprehensive);
                    setMemoryLeaks(leaks);
                  }, 100);
                }}
                className="bg-blue-500 hover:bg-blue-600 text-white py-2 px-4 rounded-lg text-sm font-medium"
              >
                Force GC
              </button>
              <button
                onClick={() => {
                  if ((window as any).__memoryLeakMonitorInterval) {
                    memoryUtils.stopMemoryLeakMonitoring();
                  } else {
                    memoryUtils.startMemoryLeakMonitoring();
                  }
                }}
                className="bg-purple-500 hover:bg-purple-600 text-white py-2 px-4 rounded-lg text-sm font-medium"
              >
                {(window as any).__memoryLeakMonitorInterval ? 'Stop' : 'Start'} Leak Monitor
              </button>
              <button
                onClick={() => {
                  window.location.reload();
                }}
                className="bg-yellow-500 hover:bg-yellow-600 text-white py-2 px-4 rounded-lg text-sm font-medium"
              >
                Reload Page
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Floating monitor (original behavior)
  if (!memoryInfo || !isVisible) {
    return (
      <button
        onClick={() => setIsVisible(true)}
        className="fixed bottom-4 right-4 bg-blue-500 text-white p-2 rounded-full text-xs z-50"
        title="Show Memory Monitor"
      >
        💾
      </button>
    );
  }

  const usagePercentage = (memoryInfo.used / memoryInfo.limit) * 100;
  const isHighUsage = usagePercentage > 80;

  return (
    <div className="fixed bottom-4 right-4 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg p-3 shadow-lg z-50 min-w-[320px] max-w-[400px]">
      <div className="flex justify-between items-center mb-3">
        <h3 className="text-sm font-semibold">Performance Monitor</h3>
        <div className="flex items-center space-x-2">
          <a 
            href="/monitor" 
            className="text-blue-500 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 text-xs"
            title="Open full monitor"
          >
            📊
          </a>
          <button
            onClick={() => setIsVisible(false)}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          >
            ✕
          </button>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex space-x-1 mb-3">
        <button
          onClick={() => setActiveTab('memory')}
          className={`px-2 py-1 text-xs rounded ${
            activeTab === 'memory' 
              ? 'bg-blue-500 text-white' 
              : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
          }`}
        >
          Memory
        </button>
        <button
          onClick={() => setActiveTab('network')}
          className={`px-2 py-1 text-xs rounded ${
            activeTab === 'network' 
              ? 'bg-blue-500 text-white' 
              : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
          }`}
        >
          Network
        </button>
        <button
          onClick={() => setActiveTab('cache')}
          className={`px-2 py-1 text-xs rounded ${
            activeTab === 'cache' 
              ? 'bg-blue-500 text-white' 
              : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
          }`}
        >
          Cache
        </button>
        <button
          onClick={() => setActiveTab('leaks')}
          className={`px-2 py-1 text-xs rounded ${
            activeTab === 'leaks' 
              ? 'bg-blue-500 text-white' 
              : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
          }`}
        >
          Leaks
        </button>
      </div>

      {/* Memory Tab */}
      {activeTab === 'memory' && (
        <div className="space-y-2 text-xs">
          <div className="flex justify-between">
            <span>JS Used:</span>
            <span className={isHighUsage ? 'text-red-500 font-semibold' : ''}>
              {memoryInfo.used}MB
            </span>
          </div>
          <div className="flex justify-between">
            <span>JS Total:</span>
            <span>{memoryInfo.total}MB</span>
          </div>
          <div className="flex justify-between">
            <span>JS Limit:</span>
            <span>{memoryInfo.limit}MB</span>
          </div>
          <div className="flex justify-between">
            <span>Usage:</span>
            <span className={isHighUsage ? 'text-red-500 font-semibold' : ''}>
              {usagePercentage.toFixed(1)}%
            </span>
          </div>
          
          {/* Memory Bar */}
          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
            <div 
              className={`h-2 rounded-full ${
                isHighUsage ? 'bg-red-500' : 'bg-green-500'
              }`}
              style={{ width: `${Math.min(usagePercentage, 100)}%` }}
            ></div>
          </div>

          {/* Comprehensive Memory Info */}
          {comprehensiveInfo && (
            <div className="mt-3 pt-2 border-t border-gray-200 dark:border-gray-600">
              <div className="flex justify-between">
                <span>DOM Nodes:</span>
                <span>{comprehensiveInfo.domNodeCount}</span>
              </div>
              <div className="flex justify-between">
                <span>Event Listeners:</span>
                <span>{comprehensiveInfo.eventListenerCount}</span>
              </div>
              <div className="flex justify-between">
                <span>Images:</span>
                <span>{comprehensiveInfo.imageMemory.imageCount}/{comprehensiveInfo.imageMemory.totalImages}</span>
              </div>
              <div className="flex justify-between">
                <span>Image Memory:</span>
                <span>{comprehensiveInfo.imageMemory.totalSize}MB</span>
              </div>
              <div className="flex justify-between">
                <span>Storage:</span>
                <span>{comprehensiveInfo.storageUsage.localStorage + comprehensiveInfo.storageUsage.sessionStorage}KB</span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Network Tab */}
      {activeTab === 'network' && (
        <div className="space-y-2 text-xs">
          <div className="flex justify-between">
            <span>Total Requests:</span>
            <span>{networkInfo.totalRequests}</span>
          </div>
          <div className="flex justify-between">
            <span>Active Requests:</span>
            <span className={networkInfo.activeRequests > 5 ? 'text-yellow-500' : ''}>
              {networkInfo.activeRequests}
            </span>
          </div>
          <div className="flex justify-between">
            <span>Cache Hits:</span>
            <span className="text-green-500">{networkInfo.cacheHits}</span>
          </div>
          <div className="flex justify-between">
            <span>Cache Misses:</span>
            <span className="text-red-500">{networkInfo.cacheMisses}</span>
          </div>
        </div>
      )}

      {/* Cache Tab */}
      {activeTab === 'cache' && (
        <div className="space-y-2 text-xs">
          {getCacheInfo().map((cache, index) => (
            <div key={index} className="flex justify-between items-center">
              <span className="truncate max-w-[150px]" title={cache.key}>
                {cache.key.split('_').pop() || cache.key}
              </span>
              <div className="flex items-center space-x-2">
                <span className={cache.hasData ? 'text-green-500' : 'text-gray-400'}>
                  {cache.hasData ? '●' : '○'}
                </span>
                <span className="text-xs text-gray-500">
                  {cache.size}B
                </span>
                {cache.extra && (
                  <span className="text-xs text-blue-500" title={cache.extra}>
                    ({cache.extra})
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Leaks Tab */}
      {activeTab === 'leaks' && (
        <div className="space-y-2 text-xs">
          {memoryLeaks.length === 0 ? (
            <div className="text-green-500 text-center py-2">
              ✅ No memory leaks detected
            </div>
          ) : (
            <div className="space-y-1">
              {memoryLeaks.map((leak, index) => (
                <div key={index} className="text-red-500 bg-red-50 dark:bg-red-900/20 p-2 rounded">
                  ⚠️ {leak}
                </div>
              ))}
            </div>
          )}
          
          {comprehensiveInfo && (
            <div className="mt-3 pt-2 border-t border-gray-200 dark:border-gray-600">
              <div className="text-xs text-gray-600 dark:text-gray-400">
                <div>Note: Chrome tab memory includes:</div>
                <div>• JavaScript heap (shown above)</div>
                <div>• DOM nodes & layout</div>
                <div>• Images, fonts, resources</div>
                <div>• Network cache</div>
                <div>• Extensions & more</div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Action Buttons */}
      <div className="mt-3 space-y-1">
        <button
          onClick={handleClearCaches}
          className="w-full bg-red-500 hover:bg-red-600 text-white text-xs py-1 px-2 rounded"
        >
          Clear All Caches
        </button>
        <button
          onClick={() => {
            memoryUtils.forceGC();
            setTimeout(() => {
              const info = memoryUtils.getMemoryInfo();
              const comprehensive = memoryUtils.getComprehensiveMemoryInfo();
              const leaks = memoryUtils.checkForMemoryLeaks();
              
              setMemoryInfo(info);
              setComprehensiveInfo(comprehensive);
              setMemoryLeaks(leaks);
            }, 100);
          }}
          className="w-full bg-blue-500 hover:bg-blue-600 text-white text-xs py-1 px-2 rounded"
        >
          Force GC
        </button>
        <button
          onClick={() => {
            if ((window as any).__memoryLeakMonitorInterval) {
              memoryUtils.stopMemoryLeakMonitoring();
            } else {
              memoryUtils.startMemoryLeakMonitoring();
            }
          }}
          className="w-full bg-purple-500 hover:bg-purple-600 text-white text-xs py-1 px-2 rounded"
        >
          {(window as any).__memoryLeakMonitorInterval ? 'Stop' : 'Start'} Leak Monitor
        </button>
        <button
          onClick={() => {
            // Reload the page to reset everything
            window.location.reload();
          }}
          className="w-full bg-yellow-500 hover:bg-yellow-600 text-white text-xs py-1 px-2 rounded"
        >
          Reload Page
        </button>
      </div>
    </div>
  );
}; 